import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Eyebrow } from "./Reveal";
import { useContent } from "../ContentContext";

export default function Gallery() {
  const { gallery, projects, company } = useContent();
  const [active, setActive] = useState(null);
  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32" style={{ background: "var(--bone)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <Eyebrow>Our Work</Eyebrow>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95]"
              style={{ color: "var(--pine)" }}>
            Project <span className="font-serif-a italic normal-case font-medium" style={{ color: "var(--brass)" }}>gallery</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {gallery.map((g, i) => (
            <motion.button key={g.id || i} data-testid={`gallery-item-${i}`} onClick={() => setActive({ type: "image", src: g.src })}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className={`relative overflow-hidden rounded-2xl group ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
              <img src={g.src} alt={`${g.tag} — tree service project`} loading="lazy"
                   className={`w-full object-cover transition-transform duration-700 group-hover:scale-110 ${i === 0 ? "h-full min-h-[220px]" : "h-44 md:h-56"}`} />
              <div className="absolute inset-0 flex items-end p-4"
                   style={{ background: "linear-gradient(to top, rgba(10,31,22,0.75), transparent 60%)" }}>
                <span className="text-xs font-semibold tracking-[0.15em] uppercase" style={{ color: "var(--brass)" }}>{g.tag}</span>
              </div>
            </motion.button>
          ))}
        </div>

        {projects.length > 0 && (
          <>
            <h3 className="mt-20 mb-8 font-display font-bold uppercase tracking-tight text-2xl" style={{ color: "var(--pine)" }}>
              Before &amp; After
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {projects.map((p, i) => (
                <motion.div key={p.id || i} data-testid={`project-${i}`}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.6 }} className="rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <img src={p.before} alt={`${p.title} before`} className="w-full h-48 object-cover" />
                      <span className="absolute top-2 left-2 text-[10px] font-bold uppercase px-2 py-1 rounded" style={{ background: "var(--pine)", color: "var(--brass)" }}>Before</span>
                    </div>
                    <div className="relative">
                      <img src={p.after} alt={`${p.title} after`} className="w-full h-48 object-cover" />
                      <span className="absolute top-2 left-2 text-[10px] font-bold uppercase px-2 py-1 rounded" style={{ background: "var(--sun)", color: "var(--pine)" }}>After</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white">
                    <div className="font-display font-bold uppercase text-sm" style={{ color: "var(--pine)" }}>{p.title}</div>
                    <div className="text-xs" style={{ color: "var(--brass)" }}>{p.category}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {company.video_url && (
          <div className="mt-16" data-testid="video-section">
            <h3 className="mb-8 font-display font-bold uppercase tracking-tight text-2xl" style={{ color: "var(--pine)" }}>
              Featured <span className="font-serif-a italic normal-case font-medium" style={{ color: "var(--brass)" }}>video</span>
            </h3>
            <motion.button data-testid="video-card" onClick={() => setActive({ type: "video", src: company.video_url })}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative w-full sm:max-w-md block overflow-hidden rounded-2xl group border" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
              <img src={company.video_poster || ""} alt="Tree removal project video" loading="lazy"
                   className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(10,31,22,0.35)" }}>
                <span className="w-20 h-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ background: "var(--sun)", boxShadow: "0 8px 30px rgba(0,0,0,0.45)" }}>
                  <Play size={30} style={{ color: "var(--pine)", marginLeft: 4 }} fill="var(--pine)" />
                </span>
              </span>
              <span className="absolute bottom-0 left-0 right-0 p-4 text-left"
                    style={{ background: "linear-gradient(to top, rgba(10,31,22,0.85), transparent)" }}>
                <span className="text-xs font-semibold tracking-[0.15em] uppercase" style={{ color: "var(--sun)" }}>Watch our work in action</span>
              </span>
            </motion.button>
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="gallery-lightbox" className="max-w-4xl p-0 overflow-hidden border-0 bg-transparent">
          <DialogTitle className="sr-only">Project media</DialogTitle>
          {active?.type === "video" ? (
            <video src={active.src} poster={company.video_poster} controls autoPlay playsInline preload="none"
                   data-testid="video-player" className="w-full h-auto max-h-[85vh] rounded-lg bg-black" />
          ) : active ? (
            <img src={active.src} alt="Project" className="w-full h-auto rounded-lg" />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
